/**
 * Published listing fees as integer subunits for the order rail.
 *
 * This is display of listing publicData, not a cart charge. Unset, negative, or
 * non-integer fees coerce to 0 the same way server/api-util/cartShipping.js does.
 * When destination is set, zone fees are resolved. When unset (or US without a
 * state), `shipping` stays the default for cart-delta math and `shippingMin` /
 * `shippingMax` expose the first-item range across default + zones.
 */

import { isCheckoutShippingDestinationSet } from '../../util/shippingDestination';
import { normalizeShippingRates, resolveShippingFees } from '../../util/shippingRates';

const toQty = value => {
  const n = typeof value === 'number' ? value : Number.parseInt(value, 10);
  return Number.isInteger(n) && n >= 1 ? n : 1;
};

/**
 * @param {Object} params
 * @param {{ amount: number, currency: string }|null|undefined} params.price
 * @param {Object|null|undefined} params.publicData
 * @param {number|string} [params.qty] integer ≥ 1; missing or invalid is treated as 1
 * @param {'shipping'|'pickup'|string} params.deliveryMethod
 * @param {{ country?: string, state?: string }|null} [params.destination]
 * @returns {{
 *   currency: string,
 *   deliveryMethod: 'shipping'|'pickup',
 *   item: number,
 *   shipping: number,
 *   additional: number,
 *   total: number
 * }|null}
 */
export const listingPriceBreakdown = ({ price, publicData, qty, deliveryMethod, destination }) => {
  const amount = price?.amount;
  const currency = price?.currency;
  if (!Number.isInteger(amount) || amount < 0 || typeof currency !== 'string' || !currency) {
    return null;
  }
  if (deliveryMethod !== 'shipping' && deliveryMethod !== 'pickup') {
    return null;
  }

  const quantity = toQty(qty);
  const item = amount * quantity;

  if (deliveryMethod === 'pickup') {
    return {
      currency,
      deliveryMethod: 'pickup',
      item,
      shipping: 0,
      additional: 0,
      total: item,
    };
  }

  const destSet = isCheckoutShippingDestinationSet(destination);
  const resolved = resolveShippingFees(publicData, destSet ? destination : {});
  const firstItem = resolved.shippingPriceInSubunitsOneItem;
  const additional = resolved.shippingPriceInSubunitsAdditionalItems;
  const shipping = firstItem + additional * (quantity - 1);

  const rates = destSet ? null : normalizeShippingRates(publicData);
  const quotes = rates
    ? [rates.default, ...rates.zones].map(fees => ({
        shipping:
          fees.shippingPriceInSubunitsOneItem +
          fees.shippingPriceInSubunitsAdditionalItems * (quantity - 1),
        additional: fees.shippingPriceInSubunitsAdditionalItems,
      }))
    : [{ shipping, additional }];
  const shippingMin = Math.min(...quotes.map(quote => quote.shipping));
  const shippingMax = Math.max(...quotes.map(quote => quote.shipping));
  const additionalMin = Math.min(...quotes.map(quote => quote.additional));
  const additionalMax = Math.max(...quotes.map(quote => quote.additional));

  return {
    currency,
    deliveryMethod: 'shipping',
    item,
    shipping,
    additional,
    total: item + shipping,
    shippingMin,
    shippingMax,
    additionalMin,
    additionalMax,
    totalMin: item + shippingMin,
    totalMax: item + shippingMax,
  };
};
